import React from 'react';
import Icon from '../../../components/AppIcon';

const ViewNegotiationModal = ({ isOpen, onClose, negotiation }) => {
  if (!isOpen || !negotiation) return null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString)?.toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning/10 text-warning border-warning/20',
      partial: 'bg-info/10 text-info border-info/20',
      paid: 'bg-success/10 text-success border-success/20',
      overdue: 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || colors?.pending;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendente',
      partial: 'Parcial',
      paid: 'Pago',
      overdue: 'Atrasado'
    };
    return labels?.[status] || status;
  };

  const saleValue = parseFloat(negotiation?.sale_value || 0);
  const cost = parseFloat(negotiation?.supplier_cost || 0);
  const profit = saleValue - cost;
  const margin = saleValue > 0 ? ((profit / saleValue) * 100)?.toFixed(1) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Eye" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Detalhes da Negociação</h2>
              <p className="text-sm text-muted-foreground">#{negotiation?.id?.slice(0, 8)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-2 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Informações Básicas</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Título</p>
                  <p className="text-base font-medium text-foreground">{negotiation?.title || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Cliente</p>
                  <p className="text-base font-medium text-foreground">{negotiation?.client?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Destino</p>
                  <p className="text-base font-medium text-foreground">{negotiation?.destination || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(negotiation?.payment_status)}`}>
                    {getStatusLabel(negotiation?.payment_status)}
                  </span>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Datas</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Data da Venda</p>
                  <p className="text-base font-medium text-foreground">{formatDate(negotiation?.sale_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Data da Viagem</p>
                  <p className="text-base font-medium text-foreground">{formatDate(negotiation?.travel_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Data de Retorno</p>
                  <p className="text-base font-medium text-foreground">{formatDate(negotiation?.return_date)}</p>
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Informações Financeiras</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Valor da Venda</p>
                  <p className="text-2xl font-bold text-foreground">{formatCurrency(saleValue)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Custo do Fornecedor</p>
                  <p className="text-2xl font-bold text-foreground">{formatCurrency(cost)}</p>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="mt-4 p-4 bg-muted/30 rounded-lg grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Lucro</p>
                  <p className="text-xl font-bold text-foreground">{formatCurrency(profit)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Margem</p>
                  <p className={`text-xl font-bold ${
                    margin >= 30 ? 'text-success' :
                    margin >= 15 ? 'text-warning' : 'text-error'
                  }`}>
                    {margin}%
                  </p>
                </div>
              </div>
            </div>

            {/* Notes */}
            {negotiation?.notes && (
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Observações</h3>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{negotiation?.notes}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-border">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg flex items-center gap-2 transition-colors"
            >
              <Icon name="X" size={18} />
              <span>Fechar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewNegotiationModal;